// ─────────────────────────────────────────────────────────────────────────
// App.tsx
// The root of the single-page app. Decides which "page" to show:
//
//   • ?job=<id> in the URL  → the driver-facing OnlineJobSheetPage
//                             (no login required, talks to job_public.php).
//   • no valid admin session → LoginView
//   • valid admin session    → AdminDashboard
//
// เดิมเช็ค session จาก sessionStorage ตรง ๆ — ตอนนี้ session อยู่ฝั่ง
// เซิร์ฟเวอร์ (PHP $_SESSION ผ่าน cookie) จึงต้องถาม API ว่ามี session
// ที่ยัง valid อยู่หรือไม่ (GET /api/auth/me.php) ตอนแอปเปิดขึ้นมาครั้งแรก
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import AdminDashboard from './AdminDashboard';
import LoginView from './LoginView';
import OnlineJobSheetPage from './OnlineJobSheetPage';
import { authApi, type ApiAdminSession } from './api';
import type { AdminSession } from './session.types';

export default function App() {
  // Driver link now carries ?token=<random> (NOT the job id — see
  // job_public.php / generate_driver_token() for why) — that route needs
  // no login at all.
  const isJobSheetRoute = new URLSearchParams(window.location.search).has('token');

  const [session, setSession] = useState<AdminSession | null>(null);
  const [checkingSession, setCheckingSession] = useState(!isJobSheetRoute);

  // ── ตรวจสอบ session ที่มีอยู่กับเซิร์ฟเวอร์ตอนเปิดแอป ──────────────────────
  useEffect(() => {
    if (isJobSheetRoute) return;
    let cancelled = false;
    authApi
      .me()
      .then((s: ApiAdminSession) => {
        if (!cancelled) setSession({ loggedIn: true, username: s.username, role: s.role, loginAt: s.loginAt });
      })
      .catch(() => {
        // ยังไม่ได้ login หรือ session หมดอายุ — ปกติ ไม่ต้องแจ้ง error
      })
      .finally(() => {
        if (!cancelled) setCheckingSession(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginSuccess = useCallback((s: AdminSession) => {
    setSession(s);
  }, []);

  const handleUnauthorized = useCallback(() => {
    setSession(null);
  }, []);

  if (isJobSheetRoute) {
    return <OnlineJobSheetPage />;
  }

  // กันหน้าจอกะพริบไปที่ LoginView ก่อนแล้วค่อยสลับไป AdminDashboard ระหว่าง
  // รอผลตรวจสอบ session ครั้งแรก
  if (checkingSession) {
    return <div className="min-h-screen bg-[#0D0225]" />;
  }

  if (!session) {
    return <LoginView onLoginSuccess={handleLoginSuccess} />;
  }

  return <AdminDashboard onUnauthorized={handleUnauthorized} />;
}
